import type { VehicleCoverages, AccidentBenefits } from 'api/quote-marketplace';
import { ACCIDENT_BENEFIT_DEFINITIONS } from './accidentBenefitsOptions';
import { isCoverageAvailable } from './provinceCoverageAvailability';

export interface CoverageDefinition {
  key: string;
  name: string;
  description: string;
  isRecommended: boolean;
}

export const LIABILITY_COVERAGE: CoverageDefinition = {
  key: 'liability_limit',
  name: 'Liability Coverage',
  description:
    "A mandatory coverage that protects you if you're sued (or threatened to be sued) for a physical injury or damage to someone else's property as a result of owning, leasing, or driving your vehicle. It covers the amount of money you have to pay to settle a lawsuit including legal fees and other related expenses, subject to the limit on your policy.",
  isRecommended: true,
};

export const DCPD_COVERAGE: CoverageDefinition = {
  key: 'dcpd_deductible',
  name: 'Direct Compensation - Property Damage',
  description:
    "Covers damage to your vehicle and its contents when another driver is at fault. This coverage allows you to claim directly from your own insurer rather than pursuing the at-fault driver's insurance company.",
  isRecommended: true,
};

export const ALL_PERILS_COVERAGE: CoverageDefinition = {
  key: 'all_perils',
  name: 'All Perils',
  description:
    'This coverage is the broadest type of physical damage coverage you can buy. It combines the protection of both Collision and Comprehensive coverage and includes coverage for loss or damage caused by a person who lives in your home who steals your vehicle.',
  isRecommended: true,
};

export const COLLISION_COVERAGE: CoverageDefinition = {
  key: 'collision',
  name: 'Collision Coverage',
  description:
    "An optional coverage designed to cover the cost of repairing or replacing your damaged vehicle when you're involved in a collision that is your fault.",
  isRecommended: false,
};

export const COMPREHENSIVE_COVERAGE: CoverageDefinition = {
  key: 'comprehensive',
  name: 'Comprehensive Coverage',
  description:
    'An optional coverage which covers damage to your vehicle not caused by a collision. This includes: vandalism, hail, falling objects, fire, theft, lightning, windstorms, and earthquakes.',
  isRecommended: false,
};

export const OPCF_20_COVERAGE: CoverageDefinition = {
  key: 'opcf_20',
  name: 'OPCF 20 - Loss of Use',
  description:
    'An optional coverage that will cover the cost of substitute transportation if you are unable to use your vehicle because of a claim covered under your policy. This includes hiring a taxi, renting a vehicle, public transportation or any combination of the above.',
  isRecommended: true,
};

export const OPCF_27_COVERAGE: CoverageDefinition = {
  key: 'opcf_27',
  name: 'OPCF 27 - Non-Owned Automobile',
  description:
    'An optional coverage that provides a transfer of coverage from your current automobile policy with a specified limit along with the applicable deductible to a rental vehicle.',
  isRecommended: true,
};

export const OPCF_43_COVERAGE: CoverageDefinition = {
  key: 'opcf_43',
  name: 'OPCF 43 - Waiver of Depreciation',
  description:
    'An optional coverage that waives the cost of depreciation from your vehicle when settling a claim.',
  isRecommended: true,
};

export const ACCIDENT_WAIVER_COVERAGE: CoverageDefinition = {
  key: 'accident_waiver',
  name: 'Accident Waiver/Forgiveness',
  description:
    'An optional coverage which prevents your premium from increasing after your first at-fault accident. Without the Accident Rating Waiver, in the event of an at-fault accident or claim, your insurance premiums could potentially see a large increase upon renewal.',
  isRecommended: true,
};

export const MINOR_CONVICTION_COVERAGE: CoverageDefinition = {
  key: 'minor_conviction_protection',
  name: 'Minor Conviction Protection',
  description:
    'An optional coverage that protects your premium from increasing due to minor traffic convictions.',
  isRecommended: false,
};

export const OPCF_2_COVERAGE: CoverageDefinition = {
  key: 'opcf_2',
  name: '#2 Drive Other Automobiles',
  description: '',
  isRecommended: false,
};

export const OPCF_6_COVERAGE: CoverageDefinition = {
  key: 'opcf_6',
  name: 'OPCF6 Permission to Rent/Lease',
  description: '',
  isRecommended: false,
};

export const OPCF_6A_PASSENGERS_COVERAGE: CoverageDefinition = {
  key: 'opcf_6a_passengers',
  name: '#6a Permission for Carry Passengers',
  description: '',
  isRecommended: false,
};

export const OPCF_8_COVERAGE: CoverageDefinition = {
  key: 'opcf_8',
  name: '#8 Property Damage Reimbursement',
  description: '',
  isRecommended: false,
};

export const OPCF_8A_COVERAGE: CoverageDefinition = {
  key: 'opcf_8a',
  name: '#8a Named Person Property Damage Reimbursement',
  description: '',
  isRecommended: false,
};

export const OPCF_4_MIN_COVERAGE: CoverageDefinition = {
  key: 'opcf_4_min',
  name: '#4min Transportation Network',
  description: '',
  isRecommended: false,
};

export const OPCF_19A_COVERAGE: CoverageDefinition = {
  key: 'opcf_19a',
  name: '#19a',
  description: '',
  isRecommended: false,
};

export const OPCF_20_DISAPPEARING_COVERAGE: CoverageDefinition = {
  key: 'opcf_20_disappearing',
  name: '#20 Disappearing Deductible',
  description: '',
  isRecommended: false,
};

export const OPCF_35_COVERAGE: CoverageDefinition = {
  key: 'opcf_35',
  name: '#35 Emergency Roadside Service',
  description: '',
  isRecommended: false,
};

export const OPCF_44_COVERAGE: CoverageDefinition = {
  key: 'opcf_44',
  name: '#44 Family Protection',
  description: '',
  isRecommended: true,
};

export const OPCF_48_COVERAGE: CoverageDefinition = {
  key: 'opcf_48',
  name: '#48 Added Coverage to Offset Tort Deductibles',
  description: '',
  isRecommended: false,
};

export const OPCF_49_COVERAGE: CoverageDefinition = {
  key: 'opcf_49',
  name: '#49 Agreement Not to Recover for Loss or Damage from an Automobile Collision',
  description: '',
  isRecommended: false,
};

export const WAIVER_OF_DEDUCTIBLE_COVERAGE: CoverageDefinition = {
  key: 'waiver_of_deductible',
  name: 'Waiver of deductible (claim specific)',
  description: '',
  isRecommended: false,
};

export const CARSHARING_COVERAGE: CoverageDefinition = {
  key: 'carsharing',
  name: 'Permission to rent or lease the vehicle for carsharing',
  description: '',
  isRecommended: false,
};

export const RECOMMENDED_LIABILITY_LIMIT = '2000000';
export const RECOMMENDED_LIABILITY_LIMIT_NUM = 2000000;

export const parseLiabilityLimitValue = (label: string): number | null => {
  if (!label) return null;
  const cleaned = label.replace(/[$,]/g, '');
  const num = parseInt(cleaned, 10);
  return isNaN(num) ? null : num;
};

export const isLiabilityBelowRecommended = (
  code: string,
  getLabel: (code: string, field: 'liability_limit_cad') => string,
): boolean => {
  if (!code) return false;
  const label = getLabel(code, 'liability_limit_cad');
  if (label.toLowerCase() === 'other') return false;
  const value = parseLiabilityLimitValue(label);
  if (value === null) return false;
  return value < RECOMMENDED_LIABILITY_LIMIT_NUM;
};

export interface DeclinedCoverage {
  name: string;
  reason: string;
}

export const getDeclinedRecommendedCoverages = (
  coverages: VehicleCoverages,
  provinceCode?: number,
  getLabel?: (code: string, field: 'liability_limit_cad') => string,
): DeclinedCoverage[] => {
  const declined: DeclinedCoverage[] = [];

  // Liability not selected or below recommended limit
  if (!coverages.liability_limit_cad) {
    declined.push({
      name: LIABILITY_COVERAGE.name,
      reason: 'Coverage not selected',
    });
  } else if (getLabel) {
    const isBelowRecommended = isLiabilityBelowRecommended(
      coverages.liability_limit_cad,
      getLabel,
    );
    if (isBelowRecommended) {
      declined.push({
        name: 'Liability Coverage ($2,000,000 recommended)',
        reason: 'Liability limit below recommended $2,000,000',
      });
    }
  }

  // DCPD not selected - only for provinces where DCPD is available
  if (
    isCoverageAvailable('dcpd_deductible', provinceCode ?? 1) &&
    !coverages.dcpd_deductible_cad
  ) {
    declined.push({
      name: DCPD_COVERAGE.name,
      reason: 'Coverage declined',
    });
  }

  // Physical damage - need at least one of All Perils, Collision, or Comprehensive
  const hasPhysicalDamage =
    coverages.all_perils_enabled ||
    coverages.collision_enabled ||
    coverages.comprehensive_enabled;

  if (!hasPhysicalDamage) {
    declined.push({
      name: 'Physical Damage Coverage',
      reason: 'No physical damage coverage selected',
    });
  }

  // OPCF 20 - Loss of Use
  if (!coverages.opcf_20_enabled) {
    declined.push({
      name: OPCF_20_COVERAGE.name,
      reason: 'Coverage not selected',
    });
  }

  // OPCF 27 - Non-Owned Automobile
  if (!coverages.opcf_27_enabled) {
    declined.push({
      name: OPCF_27_COVERAGE.name,
      reason: 'Coverage not selected',
    });
  }

  // OPCF 43 - Waiver of Depreciation
  if (!coverages.opcf_43_enabled) {
    declined.push({
      name: OPCF_43_COVERAGE.name,
      reason: 'Coverage not selected',
    });
  }

  // Accident Waiver
  if (!coverages.accident_waiver) {
    declined.push({
      name: ACCIDENT_WAIVER_COVERAGE.name,
      reason: 'Coverage not selected',
    });
  }

  // OPCF 44 - Family Protection
  if (!coverages.opcf_44_enabled) {
    declined.push({
      name: OPCF_44_COVERAGE.name,
      reason: 'Coverage not selected',
    });
  }

  return declined;
};

export const getDeclinedOptionalAccidentBenefits = (
  accidentBenefits: AccidentBenefits | undefined,
): string[] => {
  if (!accidentBenefits) return [];

  const declined: string[] = [];

  for (const def of ACCIDENT_BENEFIT_DEFINITIONS) {
    if (def.isRecommended && !accidentBenefits[def.enabledKey]) {
      declined.push(def.name);
    }
  }

  return declined;
};

export const COVERAGE_ATTESTATION_TEXT =
  'I have advised my client of the above coverage and their importance. Client has still declined the above coverages.';
